import { useState, useEffect } from "react";

/**
 * Custom hook to detect iOS keyboard visibility using the visualViewport API.
 * Works on iOS Safari to detect when the virtual keyboard is open.
 *
 * @returns {Object} - { isKeyboardOpen: boolean, keyboardHeight: number }
 * - isKeyboardOpen: true when the keyboard is visible
 * - keyboardHeight: the height of the keyboard in pixels (0 when closed)
 */
const useIOSKeyboard = () => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    // Store the initial window height for comparison
    const initialHeight = window.innerHeight;

    const handleViewportChange = () => {
      // Calculate keyboard height from viewport difference
      const heightDiff = initialHeight - viewport.height;

      // If the difference is significant (> 100px), keyboard is likely open
      // Using 100px as threshold to avoid false positives from address bar changes
      const isOpen = heightDiff > 100;

      setIsKeyboardOpen(isOpen);
      setKeyboardHeight(isOpen ? heightDiff : 0);
    };

    // Listen to both resize and scroll events
    // resize: triggered when viewport size changes (keyboard open/close)
    // scroll: triggered when viewport scrolls (iOS scrolls to keep input visible)
    viewport.addEventListener("resize", handleViewportChange);
    viewport.addEventListener("scroll", handleViewportChange);

    // Initial check
    handleViewportChange();

    return () => {
      viewport.removeEventListener("resize", handleViewportChange);
      viewport.removeEventListener("scroll", handleViewportChange);
    };
  }, []);

  return { isKeyboardOpen, keyboardHeight };
};

export default useIOSKeyboard;
